import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  List,
  Popover,
  Tab,
  Tabs,
  Typography,
  Link,
} from "@mui/material";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { NewTab } from "src/icons/new-tab";
import RecentNotificationListItem from "../molecules/recent-notification-list-item";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { t } from "i18next";
import { BellWithSprinkle } from "src/icons/bell-with-sprinkle";
import { ActionsTableMenu } from "src/components/atoms/actions-table-menu";
import { NotificationI } from "../../types/responses/notification.response";
import { FetchNextPageOptions, InfiniteQueryObserverResult } from "@tanstack/react-query";
import { PaginatedDataV4 } from "src/types/responses/paginated-data-v4";
import { ApiError } from "src/types/interfaces/api-error";
import { LoaderIcon } from "react-hot-toast";
import { NotificationSearchState } from "../../types/payloads/notification-search-state.payload";

type Props = {
  open: boolean;
  handleClose: () => void;
  anchorRef: RefObject<HTMLButtonElement>;
  notifications: NotificationI[];
  hasNextPage: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<PaginatedDataV4<NotificationI>, ApiError>>;
  isLoading: boolean;
  setSearchState: Dispatch<SetStateAction<NotificationSearchState>>;
  onNotificationMarkAsRead: (token: string) => void;
  onNotificationsMarkAsRead: (filter?: {}) => void;
  onNotificationMarkAsUnread: (token: string) => void;
  isChangingReadStatusDisabled: boolean;
};

export default function RecentNotificationsList({
  open,
  handleClose,
  anchorRef,
  notifications,
  hasNextPage,
  fetchNextPage,
  isLoading,
  setSearchState,
  isChangingReadStatusDisabled,
  onNotificationMarkAsRead,
  onNotificationMarkAsUnread,
  onNotificationsMarkAsRead,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<"all" | "unread">("all");

  const [scrollContainerEl, setScrollContainerEl] = useState<HTMLUListElement | null>(null);
  const [loadMoreEl, setLoadMoreEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scrollContainerEl || !loadMoreEl) return;
    if (!hasNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        const firstEntry = entries[0];

        if (firstEntry?.isIntersecting && hasNextPage && !isLoading) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainerEl,
        rootMargin: "120px",
        threshold: 0,
      },
    );

    observer.observe(loadMoreEl);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isLoading, scrollContainerEl, loadMoreEl]);

  const actionsSlot = useCallback(
    (unreadNotificationsCount: number) => {
      const actions = [
        {
          label: t("notifications:common.actions.markAllAsRead"),
          id: "notifications_mark_all_as_read_action_menu_approve",
          onClick: () => {
            onNotificationsMarkAsRead();
          },
          disabled: !unreadNotificationsCount || isChangingReadStatusDisabled,
          tooltip: t("notifications:common.allNotificationsRead"),
        },
        {
          label: t("notifications:common.actions.settings"),
          id: "notifications_settings_navigation_action",
          onClick: () => {
            navigate("/settings/notifications");
            handleClose();
          },
          disabled: false,
        },
      ];
      return <ActionsTableMenu actions={actions} />;
    },
    [notifications],
  );

  const unreadNotificationsCount = useMemo(() => {
    return notifications?.filter(notification => !notification.isRead)?.length ?? 0;
  }, [notifications]);

  return (
    <Popover
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      open={open}
      onClose={handleClose}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}>
      <Card sx={{ px: 0, pt: 3, pb: 0, minWidth: "513px", maxWidth: "513px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
          }}>
          <Typography variant="h4">{t("notifications:common.title")}</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 0.5,
            }}>
            <RouterLink to="/notifications" target="_blank">
              <IconButton size="small">
                <NewTab />
              </IconButton>
            </RouterLink>
            {actionsSlot(unreadNotificationsCount)}
          </Box>
        </Box>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Tabs
            value={selectedTab}
            sx={{ mt: 2 }}
            variant="scrollable"
            allowScrollButtonsMobile
            onChange={(_e, tab) => {
              setSelectedTab(tab);
              if (tab === "all") {
                setSearchState(prev => {
                  const { read, ...rest } = prev;
                  return rest;
                });
              } else {
                setSearchState(prev => ({ ...prev, read: false }));
              }
            }}
            style={{ padding: "0px 24px" }}>
            <Tab label={t("notifications:common.allNotifications")} value="all" />
            <Tab label={t("notifications:common.unread")} value="unread" />
          </Tabs>

          <Divider sx={{ mb: 0, mx: 3 }} />

          <List
            sx={{
              maxHeight: "65vh",
              height: "550px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
            ref={setScrollContainerEl}>
            {notifications?.map(notification => (
              <RecentNotificationListItem
                key={notification.token}
                notification={notification}
                isChangingReadStatusDisabled={isChangingReadStatusDisabled}
                onNotificationMarkAsUnread={onNotificationMarkAsUnread}
                onNotificationMarkAsRead={onNotificationMarkAsRead}
                handleClose={handleClose}
              />
            ))}
            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  paddingTop: 3,
                  paddingBottom: 1,
                }}>
                <LoaderIcon style={{ width: 24, height: 24 }} />
              </Box>
            )}
            <Box ref={setLoadMoreEl} sx={{ height: "1px" }} />

            {!hasNextPage && notifications?.length > 0 ? (
              <NotificationsSummary />
            ) : !hasNextPage && !isLoading && selectedTab === "unread" ? (
              <AllNotificationsReadSummary />
            ) : (
              !hasNextPage && !isLoading && <NoNotificationsSummary />
            )}
          </List>
        </CardContent>
      </Card>
    </Popover>
  );
}

const NotificationsSummary = () => {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 2,
        mt: "auto",
      }}>
      <Typography
        sx={{ textAlign: "center", width: 1 / 2.5 }}
        variant="body2"
        fontSize={12}
        color="textSecondary">
        {t("notifications:common.notificationsSummary")}{" "}
        <Link
          component={RouterLink}
          target="_blank"
          to="/notifications"
          sx={{
            color: "primary.main",
            textDecorationColor: "text.primary",
            textUnderlineOffset: "1.75px",
            marginLeft: 0.25,
          }}>
          {t("common:showMore")}
        </Link>
      </Typography>
    </Box>
  );
};

const AllNotificationsReadSummary = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "68vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
      }}>
      <BellWithSprinkle sx={{ width: 100, height: 100 }} />
      <Typography variant="body2" color="textSecondary">
        {t("notifications:common.allNotificationsRead")}
      </Typography>
    </Box>
  );
};

const NoNotificationsSummary = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "68vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
      }}>
      <BellWithSprinkle sx={{ width: 100, height: 100 }} />
      <Typography variant="body2" color="textSecondary">
        {t("notifications:common.noNotifications")}
      </Typography>
    </Box>
  );
};
